// pageConfig.js
const PINATA_API_KEY = process.env.PINATA_API_KEY;
const PINATA_SECRET_KEY = process.env.PINATA_SECRET_KEY;
const imageFiles = new Map();


/**
 * Upload an image to IPFS via Pinata
 * @param {File|Blob|string} fileOrBlobOrUrl - The file, blob, or URL to upload
 * @returns {Promise<string|null>} - The IPFS CID or null if upload failed
 */
async function uploadImageToIPFS(fileOrBlobOrUrl) {
    try {
        // Skip if no Pinata credentials
        if (!PINATA_API_KEY || !PINATA_SECRET_KEY) {
            console.log('Missing Pinata credentials, cannot upload image');
            return null;
        }

        let blob;

        // Handle different input types
        if (fileOrBlobOrUrl instanceof File || fileOrBlobOrUrl instanceof Blob) {
            // If it's already a File or Blob, use it directly
            blob = fileOrBlobOrUrl;
        } else if (typeof fileOrBlobOrUrl === 'string' && fileOrBlobOrUrl.startsWith('blob:')) {
            // If it's a blob URL, fetch the blob data
            const response = await fetch(fileOrBlobOrUrl);
            blob = await response.blob();
        } else {
            // Unsupported input type
            console.log('Unsupported input type for uploadImageToIPFS:', typeof fileOrBlobOrUrl);
            return null;
        }

        console.log('Preparing image data for IPFS upload...');

        // Create a FormData object for the image upload
        const formData = new FormData();
        formData.append('file', blob, 'image.png'); // Default filename

        // Add Pinata metadata
        const pinataMetadata = JSON.stringify({
            name: `page_image_${new Date().getTime()}`,
            keyvalues: {
                timestamp: new Date().toISOString()
            }
        });
        formData.append('pinataMetadata', pinataMetadata);

        // Set Pinata options
        const pinataOptions = JSON.stringify({
            cidVersion: 1
        });
        formData.append('pinataOptions', pinataOptions);

        console.log('Uploading image to Pinata...');

        // Send the image to Pinata
        const pinataResponse = await fetch('https://api.pinata.cloud/pinning/pinFileToIPFS', {
            method: 'POST',
            headers: {
                'pinata_api_key': PINATA_API_KEY,
                'pinata_secret_api_key': PINATA_SECRET_KEY
            },
            body: formData
        });

        if (!pinataResponse.ok) {
            const errorBody = await pinataResponse.text();
            throw new Error(`Failed to upload image to IPFS. Status: ${pinataResponse.status}. Body: ${errorBody}`);
        }

        const responseData = await pinataResponse.json();
        return responseData.IpfsHash;
    } catch (error) {
        console.error('Error uploading image to IPFS:', error);
        return null;
    }
}

/**
 * Upload metadata to IPFS via Pinata
 * @param {Object} metadata - The metadata to upload
 * @returns {Promise<string>} - The IPFS CID
 */
async function uploadPageMetadataToIPFS(metadata) {
    if (!metadata) {
        throw new Error('Cannot upload undefined metadata to IPFS');
    }

    const pinataApiUrl = 'https://api.pinata.cloud/pinning/pinJSONToIPFS';

    if (!PINATA_API_KEY || !PINATA_SECRET_KEY) {
        throw new Error('Missing Pinata credentials');
    }

    // Make sure we have an owner property somewhere in the metadata
    // Check in multiple places where it might be stored
    let owner = 'unknown';

    if (metadata.properties && metadata.properties.owner) {
        owner = metadata.properties.owner;
    } else if (metadata.collection && metadata.collection.creator) {
        owner = metadata.collection.creator;
    } else if (metadata.properties && metadata.properties.meta &&
        metadata.properties.meta.content && metadata.properties.meta.content.owner) {
        owner = metadata.properties.meta.content.owner;
    }

    console.log('Using owner for file name:', owner);
    const fileName = `${owner}_${metadata.name || 'page'}_metadata.json`;

    const pinataRequestBody = {
        pinataContent: metadata,
        pinataMetadata: {
            name: fileName,
            keyvalues: {
                owner: owner,
                title: metadata.name,
                timestamp: new Date().toISOString()
            }
        },
        pinataOptions: {
            cidVersion: 1
        }
    };

    try {
        console.log('Sending request to Pinata...', pinataRequestBody);
        const response = await fetch(pinataApiUrl, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'pinata_api_key': PINATA_API_KEY,
                'pinata_secret_api_key': PINATA_SECRET_KEY,
            },
            body: JSON.stringify(pinataRequestBody),
        });

        if (!response.ok) {
            const errorBody = await response.text();
            throw new Error(`Failed to upload JSON to IPFS. Status: ${response.status}. Body: ${errorBody}`);
        }

        const responseData = await response.json();
        return responseData.IpfsHash;
    } catch (error) {
        console.error('Error uploading JSON to IPFS:', error);
        throw error;
    }
}

/**
 * Create metadata for a page
 * @param {Object} page - The page data
 * @returns {Object} - The metadata object
 */
function createPageMetadata(page) {
    if (!page.title || !page.description || !page.owner) {
        throw new Error('Missing required metadata fields');
    }

    const slugTitle = page.title.toLowerCase().replace(/\s+/g, '-');

    // Use the values passed from handlePageUpdate
    const showOwnershipValue = page.showOwnership;
    const darkModeValue = page.darkMode;
    const festiveValue = page.festive;
    const festiveNFTValue = page.festiveNFT;
    const festiveNFTNameValue = page.selectedNFTName;
    const festiveNFTImageValue = page.selectedNFTImage;
    const profilePhotoValue = page.profilePhoto;
    const bnsData = page.bnsInfo || page.bns || { enabled: false, bnsName: '' };

    // CREATE BNS INFO OBJECT
    const bnsInfo = {
        enabled: bnsData.enabled,
        bnsName: bnsData.bnsName,
        resolvedAddress: page.owner,
        timestamp: bnsData.timestamp || new Date().toISOString()
    };

    console.log('createPageMetadata - toggle values:', {
        showOwnership: showOwnershipValue,
        darkMode: darkModeValue,
        festive: festiveValue,
        festiveNFT: festiveNFTValue,
        festiveNFTName: festiveNFTNameValue,
        festiveNFTImage: festiveNFTImageValue,
        profilePhoto: profilePhotoValue,
        contentBlocks: page.contentBlocks ? page.contentBlocks.length : 0,
        bns: bnsData 
    });

    // Base attributes array
    const attributes = [
        { trait_type: "ID", value: page.id },
        { trait_type: "Page Status", value: page.active ? "Active" : "Inactive" },
        { trait_type: "Show Ownership", value: showOwnershipValue ? "On" : "Off" },
        { trait_type: "Dark Mode", value: darkModeValue ? "On" : "Off" },
        { trait_type: "Festive Mode", value: festiveValue ? "On" : "Off" }
    ];

    // Add BNS attributes if enabled - ONLY ONCE
    if (bnsData.enabled && bnsData.bnsName) {
        attributes.push({
            trait_type: "BNS Name",
            value: bnsData.bnsName
        });
        
        attributes.push({
            trait_type: "BNS Enabled",
            value: "Yes"
        });
    }

    if (festiveValue && festiveNFTValue) {
        attributes.push({
            trait_type: "Festive NFT",
            value: festiveNFTValue
        });

        // Add NFT name if available
        if (festiveNFTNameValue) {
            attributes.push({
                trait_type: "Festive NFT Name",
                value: festiveNFTNameValue
            });
        }
    }

    // Add profile photo attribute if it exists and is enabled
    if (profilePhotoValue && profilePhotoValue.enabled) {
        attributes.push({
            trait_type: "Profile Photo",
            value: profilePhotoValue.src || "Set"
        });
    }

    // Process content blocks
    const processedContentBlocks = page.contentBlocks ? page.contentBlocks.map(block => {
        if (block.type === 'image') {
            if (!block.url || block.url === '' || (block.url && block.url.startsWith('blob:'))) {
                console.log('Processing image block in createPageMetadata');
                return {
                    ...block,
                    url: `ipfs://bafybeigdyrzt5sfp7udm7hu76uh7y26nf3efuylqabf3oclgtqy55fbzdi`,
                    content: `ipfs://bafybeigdyrzt5sfp7udm7hu76uh7y26nf3efuylqabf3oclgtqy55fbzdi`,
                    alt: 'Content image (distinct from profile)'
                };
            }
        }
        return block;
    }) : [];

    // Get NFT collection information
    const nftCollection = page.metadata?.nftCollection ? {
        enabled: true,
        name: page.metadata.nftCollection.name || '',
        items: Array.isArray(page.metadata.nftCollection.items)
            ? page.metadata.nftCollection.items.map(nft => ({
                name: nft.name,
                image: nft.image
            }))
            : []
    } : null;

    // Create festive content object
    const festiveContent = festiveValue ? {
        enabled: true,
        nft: {
            identifier: festiveNFTValue,
            name: festiveNFTNameValue,
            imageUrl: festiveNFTImageValue
        }
    } : {
        enabled: false
    };

    // Add social links data
    const socialLinks = {
        enabled: page.socialLinks?.enabled || false,
        links: {
            x: page.socialLinks?.links?.x || '',
            discord: page.socialLinks?.links?.discord || '',
            website: page.socialLinks?.links?.website || ''
        }
    };

    // Determine the main image to use
    let mainImage = null;
    if (page.profileImageCid) {
        mainImage = `ipfs://${page.profileImageCid}`;
    } else if (profilePhotoValue && profilePhotoValue.enabled && profilePhotoValue.src) {
        mainImage = profilePhotoValue.src;
    } else if (page.imageCid) {
        mainImage = `ipfs://${page.imageCid}`;
    }

    // DETERMINE URLS BASED ON BNS - USE THE CALCULATED VALUES
    const external_url = bnsData.enabled && bnsData.bnsName 
        ? `https://gated.so/#/${bnsData.bnsName}` 
        : `https://${page.owner.toLowerCase()}.gated.so/#/${slugTitle}`;

    const content_url = bnsData.enabled && bnsData.bnsName 
        ? `gated.so/${bnsData.bnsName}` 
        : `${page.owner.toLowerCase()}.gated.so/#/${slugTitle}`;

    console.log('External URL determined:', external_url);

    // Construct the full metadata object
    const result = {
        sip: 16,
        name: page.title,
        description: page.description,
        image: mainImage,
        external_url: external_url,  // USE THE CALCULATED URL
        attributes,
        collection: {
            name: "Gated Pages",
            family: "Gated",
            creator: page.owner
        },
        properties: {
            files: {},
            attrs: {},
            owner: page.owner,
            meta: {
                blockchain: 'Stacks',
                copyright: `© ${new Date().getFullYear()} Gated.so`,
                version: "1.0",
                display: page.display || {},
                content: {
                    ...(page.content || {}),
                    slug: slugTitle,
                    url: content_url,  // USE THE CALCULATED URL
                    festive: festiveContent,
                    socialLinks: socialLinks,
                    profilePhoto: profilePhotoValue,
                    showOwnership: showOwnershipValue,
                    darkMode: darkModeValue,
                    contentBlocks: processedContentBlocks,
                    nftCollection: nftCollection,
                    bns: bnsInfo  // SINGLE BNS OBJECT
                },
                security: page.security || {}
            }
        }
    };


    // Double-check that we have a valid result before returning
    if (!result) {
        console.error('Error: metadata result is undefined');
        throw new Error('Failed to create metadata object');
    }

    return result;
}

/**
 * Handle the update of a page
 * @param {Object} updateData - The data for the update
 * @returns {Promise<Object>} - The result of the update
 */
async function handlePageUpdate(updateData) {
    console.log("Contract Type:", updateData.contractType);
    console.log("Update Data Structure:", updateData);
    console.log("Full updateData:", JSON.stringify(updateData, null, 2));

    // Destructure most fields from the UI
    const {
        id,
        title,
        description,
        active,
        owner,
        festiveNFT = null,
        selectedNFTName = null,
        selectedNFTImage = null,
        metadata = {},
    } = updateData;

      // OWNER FALLBACK - handle cases where owner is null
// OWNER FALLBACK - handle cases where owner is null
    let finalOwner = owner;
    if (!finalOwner) {
        // Try different possible locations for owner data
        finalOwner = updateData.stxAddress || 
                    updateData.walletAddress || 
                    updateData.address ||
                    updateData.metadata?.owner ||
                    updateData.metadata?.properties?.owner;
        
        console.log('Owner was null, using fallback:', finalOwner);
    }

    // If stxAddress is empty string, try to get from wallet or session
    if (!finalOwner || finalOwner === '') {
        console.log('No owner in updateData, attempting to get from wallet/session...');
        
        // Try to get from global wallet state (adjust based on your wallet integration)
        if (typeof window !== 'undefined') {
            // Try different possible global wallet objects
            if (window.stxAddress) {
                finalOwner = window.stxAddress;
                console.log('Found owner in window.stxAddress:', finalOwner);
            } else if (window.userSession && window.userSession.loadUserData) {
                try {
                    const userData = window.userSession.loadUserData();
                    finalOwner = userData.profile?.stxAddress?.mainnet || userData.profile?.stxAddress?.testnet;
                    console.log('Found owner in userSession:', finalOwner);
                } catch (e) {
                    console.log('Error getting address from userSession:', e);
                }
            } else if (window.wallet && window.wallet.address) {
                finalOwner = window.wallet.address;
                console.log('Found owner in window.wallet:', finalOwner);
            }
        }
    }

    // If still no owner, this is a critical error
   if (!finalOwner || finalOwner === '') {
        if (typeof localStorage !== 'undefined') {
            const cachedAddress = localStorage.getItem('stxAddress') || 
                                localStorage.getItem('walletAddress') || 
                                localStorage.getItem('userAddress');
            if (cachedAddress) {
                finalOwner = cachedAddress;
                console.log('Found owner in localStorage:', finalOwner);
            }
        }
    }

    // Use let for variables that might be reassigned
    let contentBlocks = updateData.content || updateData.metadata?.contentBlocks || [];

    // Handle profilePhoto separately since it might be null
    let profilePhotoData = { enabled: false, src: null };
    if (updateData.profilePhoto) {
        profilePhotoData = updateData.profilePhoto;
    } else if (updateData.metadata?.profilePhoto) {
        profilePhotoData = updateData.metadata.profilePhoto;
    }

    // IMPORTANT: Handle toggle values and other special fields separately
    let showOwnership = false;
    let darkMode = false;
    let festive = false;

    // Check for showOwnership in various formats
    if (updateData.showOwnership === true) {
        showOwnership = true;
    } else if (updateData.ownershipEnabled === true) {
        showOwnership = true;
    } else if (typeof updateData.showOwnership === "string" && updateData.showOwnership.toLowerCase() === "true") {
        showOwnership = true;
    }

    // Check for darkMode in various formats
    if (updateData.darkMode === true) {
        darkMode = true;
    } else if (updateData.darkModeEnabled === true) {
        darkMode = true;
    } else if (typeof updateData.darkMode === "string" && updateData.darkMode.toLowerCase() === "true") {
        darkMode = true;
    }

    // Check for festive mode in various formats
    if (updateData.festive === true) {
        festive = true;
    } else if (updateData.festiveMode === true) {
        festive = true;
    } else if (typeof updateData.festive === "string" && updateData.festive.toLowerCase() === "true") {
        festive = true;
    }

    // Get NFT info for festive mode
    let selectedFestiveNFT = festiveNFT;
    let selectedFestiveNFTName = selectedNFTName;
    let selectedFestiveNFTImage = selectedNFTImage;

    // DECLARE bnsData FIRST - before it's used
    let bnsData = { enabled: false, bnsName: '' };
    
    // Check for BNS data in updateData
    if (updateData.bnsData) {
        bnsData = updateData.bnsData;
    } else if (updateData.bns) {
        bnsData = updateData.bns;
    }

    // Get the actual DOM elements if available in browser context
    if (typeof document !== 'undefined') {
        // Check for ownership toggle
        const ownershipToggle = document.getElementById('ownershipEnabled');
        if (ownershipToggle) {
            console.log('Found ownership checkbox in DOM, checked state:', ownershipToggle.checked);
            showOwnership = ownershipToggle.checked;
        }

        // Check for dark mode toggle
        const darkModeToggle = document.getElementById('darkModeEnabled');
        if (darkModeToggle) {
            console.log('Found dark mode checkbox in DOM, checked state:', darkModeToggle.checked);
            darkMode = darkModeToggle.checked;
        }

        // Check for festive mode toggle
        const festiveToggle = document.getElementById('festiveMode');
        if (festiveToggle) {
            console.log('Found festive mode checkbox in DOM, checked state:', festiveToggle.checked);
            festive = festiveToggle.checked;
        }

        // Check for profile photo toggle
        const profilePhotoToggle = document.getElementById('profilePhotoEnabled');
        if (profilePhotoToggle) {
            console.log('Found profile photo checkbox in DOM, checked state:', profilePhotoToggle.checked);
            profilePhotoData.enabled = profilePhotoToggle.checked;

            // If profile photo is enabled, try to find the image URL
            if (profilePhotoData.enabled) {
                // First try to find a dedicated profile photo img element
                const profileImg = document.querySelector('#profilePhoto img');
                if (profileImg && profileImg.src) {
                    profilePhotoData.src = profileImg.src;
                    console.log('Found profile image in DOM:', profileImg.src);
                }

                // If not found, try to get from the NFT preview
                if (!profilePhotoData.src) {
                    const nftImg = document.querySelector('.nft-preview img');
                    if (nftImg && nftImg.src) {
                        profilePhotoData.src = nftImg.src;
                        console.log('Found NFT image in DOM for profile photo:', nftImg.src);
                    }
                }

                // If still not found, try specific selectors based on the HTML
                if (!profilePhotoData.src) {
                    const allImgs = document.querySelectorAll('img');
                    for (const img of allImgs) {
                        // Check if this is likely to be the profile image based on its attributes
                        if (img.alt === "Profile Image" ||
                            (img.src && img.src.includes('token-metadata-api')) ||
                            (img.className && img.className.includes('profile-img'))) {
                            profilePhotoData.src = img.src;
                            console.log('Found profile image through attribute search:', img.src);
                            break;
                        }
                    }
                }
            }
        }

        // Try to get the selected NFT for festive mode if it's enabled
        if (festive) {
            const festiveNFTSelect = document.getElementById('festiveNFT');
            if (festiveNFTSelect && festiveNFTSelect.value) {
                console.log('Found festive NFT selection in DOM:', festiveNFTSelect.value);
                selectedFestiveNFT = festiveNFTSelect.value;
            }

            // Try to find the NFT name and image from a data attribute or other source
            if (festiveNFTSelect && festiveNFTSelect.selectedOptions && festiveNFTSelect.selectedOptions[0]) {
                selectedFestiveNFTName = festiveNFTSelect.selectedOptions[0].getAttribute('data-name') || selectedFestiveNFTName;
                selectedFestiveNFTImage = festiveNFTSelect.selectedOptions[0].getAttribute('data-image') || selectedFestiveNFTImage;
            }
        }

        // Handle BNS data - NOW bnsData is already declared
        const bnsToggle = document.getElementById('bnsEnabled');
        const bnsDropdown = document.getElementById('bnsDropdown');
        
        if (bnsToggle) {
            bnsData.enabled = bnsToggle.checked;
            console.log('Found BNS toggle in DOM, checked state:', bnsData.enabled);
            
            // Clear bnsName if BNS is disabled
            if (!bnsData.enabled) {
                bnsData.bnsName = '';
                console.log('BNS disabled, clearing bnsName');
            }
        }
        
        if (bnsDropdown && bnsDropdown.value && bnsData.enabled) {
            bnsData.bnsName = bnsDropdown.value;
            console.log('Found BNS selection in DOM:', bnsData.bnsName);
        }

        // Get content blocks from the DOM
        try {
            // Process images in content blocks
            for (let i = 0; i < contentBlocks.length; i++) {
                const block = contentBlocks[i];
                if (block.type === 'image') {
                    // Get block ID for referencing stored image files
                    const blockId = block.id || `block-${i}`;

                    const storedFile = imageFiles.get(blockId);

                    // If we have a stored file, use it instead of the URL
                    if (storedFile) {
                        console.log('Found stored file for image block:', blockId);

                        // Upload the actual file to IPFS
                        const imageCid = await uploadImageToIPFS(storedFile);

                        if (imageCid) {
                            contentBlocks[i].url = `https://ipfs.io/ipfs/${imageCid}`;
                            contentBlocks[i].content = `https://ipfs.io/ipfs/${imageCid}`;
                            console.log('Image uploaded to IPFS with CID:', imageCid);
                        } else {
                            // Use fallback CID if upload failed
                            contentBlocks[i].url = `ipfs://bafkreifgvle7pvczsnxvb72j224fkxabctadqsfxl3jkrhw6gyhmrk3vua`;
                            contentBlocks[i].content = `ipfs://bafkreifgvle7pvczsnxvb72j224fkxabctadqsfxl3jkrhw6gyhmrk3vua`;
                            console.log('Failed to upload image to IPFS, using fallback CID');
                        }
                    }
                    // Handle blob URLs - these are often from newly uploaded images
                    else if (block.url && block.url.startsWith('blob:')) {
                        console.log('Found blob URL for uploaded image:', block.url);

                        try {
                            // Fetch the blob data since we don't have the file stored
                            const response = await fetch(block.url);
                            const blobData = await response.blob();

                            // Upload the blob to IPFS
                            console.log('Starting upload process for blob URL');

                            // In a production environment, use this code:
                            const imageCid = await uploadImageToIPFS(blobData);
                            if (imageCid) {
                                contentBlocks[i].url = `ipfs://${imageCid}`;
                                contentBlocks[i].content = `ipfs://${imageCid}`;
                                console.log('Image uploaded to IPFS with CID:', imageCid);
                            } else {
                                // Use fallback CID if upload failed
                                contentBlocks[i].url = `ipfs://bafybeigdyrzt5sfp7udm7hu76uh7y26nf3efuylqabf3oclgtqy55fbzdi`;
                                contentBlocks[i].content = `ipfs://bafybeigdyrzt5sfp7udm7hu76uh7y26nf3efuylqabf3oclgtqy55fbzdi`;
                                console.log('Failed to upload image to IPFS, using fallback CID');
                            }
                        } catch (error) {
                            console.error('Error handling image upload:', error);
                            // Use a fallback CID in case of error
                            contentBlocks[i].url = `ipfs://bafybeigdyrzt5sfp7udm7hu76uh7y26nf3efuylqabf3oclgtqy55fbzdi`;
                            contentBlocks[i].content = `ipfs://bafybeigdyrzt5sfp7udm7hu76uh7y26nf3efuylqabf3oclgtqy55fbzdi`;
                        }
                    } else if (!block.url || block.url === '') {
                        console.log('Found empty URL in image block');
                        // Handle empty URL case as before
                        contentBlocks[i].url = `ipfs://bafybeigdyrzt5sfp7udm7hu76uh7y26nf3efuylqabf3oclgtqy55fbzdi`;
                        contentBlocks[i].content = `ipfs://bafybeigdyrzt5sfp7udm7hu76uh7y26nf3efuylqabf3oclgtqy55fbzdi`;
                    } else if (block.url.includes('bafkreifgvle7pvczsnxvb72j224fkxabctadqsfxl3jkrhw6gyhmrk3vua')) {
                        // If it's using the profile photo URL, change it
                        contentBlocks[i].url = `ipfs://bafybeigdyrzt5sfp7udm7hu76uh7y26nf3efuylqabf3oclgtqy55fbzdi`;
                        contentBlocks[i].content = `ipfs://bafybeigdyrzt5sfp7udm7hu76uh7y26nf3efuylqabf3oclgtqy55fbzdi`;
                    }
                }
            }
        } catch (error) {
            console.error('Error processing content blocks:', error);
        }
    } // <-- Close the document check block

    console.log('Final toggle values used:', {
        showOwnership,
        darkMode,
        festive,
        selectedFestiveNFT,
        selectedFestiveNFTName,
        selectedFestiveNFTImage,
        profilePhoto: profilePhotoData
    });

    // Also check if BNS data exists in the metadata (for existing pages)
    // BUT only if BNS is still enabled and we don't already have a name
    if (bnsData.enabled && !bnsData.bnsName && updateData.metadata?.properties?.meta?.content?.bnsName) {
        bnsData.bnsName = updateData.metadata.properties.meta.content.bnsName;
        bnsData.enabled = updateData.metadata.properties.meta.content.bnsEnabled || true;
    }

    console.log('Final BNS data:', bnsData);

    const bnsInfo = {
        enabled: bnsData.enabled,
        bnsName: bnsData.bnsName,
        resolvedAddress: owner, // The Stacks address this BNS resolves to
        timestamp: new Date().toISOString() // When this association was created
    };

    console.log('Created bnsInfo object:', bnsInfo);

    // Get social links directly from updateData or from metadata as fallback
    const socialLinksData = {
        enabled: updateData.socialLinks?.enabled ?? updateData.metadata?.socialLinks?.enabled ?? false,
        links: {
            x: updateData.socialLinks?.links?.x ?? updateData.metadata?.socialLinks?.links?.x ?? '',
            discord: updateData.socialLinks?.links?.discord ?? updateData.metadata?.socialLinks?.links?.discord ?? '',
            website: updateData.socialLinks?.links?.website ?? updateData.metadata?.socialLinks?.links?.website ?? ''
        }
    };

    console.log('Starting page update process:', {
        id,
        title,
        bnsInfo,
        description,
        active,
        owner,
        festive,
        festiveNFT: selectedFestiveNFT,
        selectedNFTName: selectedFestiveNFTName,
        selectedNFTImage: selectedFestiveNFTImage,
        socialLinks: socialLinksData,
        darkMode,
        showOwnership,
        contentBlocks: contentBlocks.length,
        profilePhoto: profilePhotoData
    });

    try {
        const imageCid = "bafkreifgvle7pvczsnxvb72j224fkxabctadqsfxl3jkrhw6gyhmrk3vua";
        let profileImageCid = null;

        // If profile photo is enabled and has a URL, handle IPFS considerations
        if (profilePhotoData.enabled && profilePhotoData.src) {
            try {
                console.log('Processing profile photo URL:', profilePhotoData.src);
                // If the URL is already an IPFS URL, extract the CID
                if (profilePhotoData.src.includes('ipfs.io/ipfs/')) {
                    profileImageCid = profilePhotoData.src.split('ipfs.io/ipfs/')[1];
                    console.log('Extracted existing IPFS CID for profile photo:', profileImageCid);
                } else {
                    // In a real implementation, you would upload the image to IPFS here
                    // For now, we'll just use the URL as-is
                    console.log('Using direct image URL for profile photo:', profilePhotoData.src);
                }
            } catch (error) {
                console.error('Error processing profile photo:', error);
            }
        }

        // Check if we can get festive NFT info from metadata before creating the page metadata
        if (festive && !selectedFestiveNFT && updateData.metadata?.nftCollection?.festiveNFT) {
            console.log('Found festive NFT info in metadata:', {
                festiveNFT: updateData.metadata.nftCollection.festiveNFT,
                name: updateData.metadata.nftCollection.selectedNFTName,
                image: updateData.metadata.nftCollection.selectedNFTImage
            });

            selectedFestiveNFT = updateData.metadata.nftCollection.festiveNFT;
            selectedFestiveNFTName = updateData.metadata.nftCollection.selectedNFTName;
            selectedFestiveNFTImage = updateData.metadata.nftCollection.selectedNFTImage;
        }

           const pageData = {
            id,
            title,
            description,
            active,
            owner,
            festive,
            festiveNFT: selectedFestiveNFT,
            selectedNFTName: selectedFestiveNFTName,
            selectedNFTImage: selectedFestiveNFTImage,
            socialLinks: socialLinksData,
            imageCid,
            profileImageCid,
            darkMode,
            showOwnership,
            contentBlocks,
            profilePhoto: profilePhotoData,
            metadata,
            bnsInfo: bnsInfo
        };

        // DEBUG: Check required fields before calling createPageMetadata
        console.log('DEBUG - Input validation at start:', {
            hasTitle: !!updateData.title,
            hasDescription: !!updateData.description, 
            hasOwner: !!updateData.owner,
            inputTitle: updateData.title,
            inputDescription: updateData.description,
            inputOwner: updateData.owner
        });

        console.log('DEBUG - Required fields check in pageData:', {
            title: pageData.title,
            description: pageData.description,
            owner: pageData.owner,
            titleType: typeof pageData.title,
            descriptionType: typeof pageData.description,
            ownerType: typeof pageData.owner,
            titleLength: pageData.title ? pageData.title.length : 'null/undefined',
            descriptionLength: pageData.description ? pageData.description.length : 'null/undefined',
            ownerLength: pageData.owner ? pageData.owner.length : 'null/undefined',
            titleTruthy: !!pageData.title,
            descriptionTruthy: !!pageData.description,
            ownerTruthy: !!pageData.owner
        });

        // Create the metadata
        const pageMetadata = createPageMetadata(pageData);


        console.log('Generated metadata:', pageMetadata ? JSON.stringify(pageMetadata, null, 2) : 'undefined');

        if (!pageMetadata) {
            throw new Error('Failed to generate page metadata');
        }

        const ipfsHash = await uploadPageMetadataToIPFS(pageMetadata);
        console.log('Uploaded to IPFS:', ipfsHash);

        return {
            success: true,
            ipfsHash,
            metadata: pageMetadata
        };
    } catch (error) {
        console.error('Error in page update process:', error);
        throw error;
    }
}

export {
    handlePageUpdate,
    imageFiles
};